import { motion } from "framer-motion";
import { ArrowUpRight, Check } from "lucide-react";
import { SERVICES } from "@/data/site";
import { SectionHeader, EASE } from "@/components/motion/Reveal";

const ServiceCard = ({ s, i, onBook, wide }) => {
  const Icon = s.icon;
  return (
    <motion.article
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-8% 0px" }}
      transition={{ duration: 0.8, delay: (i % 3) * 0.1, ease: EASE }}
      whileHover={{ y: -6 }}
      className={`group relative flex flex-col bg-panel/80 ${wide ? "sm:col-span-2 lg:col-span-3 lg:flex-row lg:items-center lg:gap-12" : ""} border border-white/10 hover:border-brand/50 rounded-xl p-6 sm:p-8 transition-[border-color,box-shadow] duration-300 hover:shadow-[0_20px_50px_rgba(27,114,217,0.12)] overflow-hidden`}
      data-testid={`service-card-${i + 1}`}
    >
      <div className="absolute -right-6 -top-8 font-display font-black text-[7rem] leading-none text-white/[0.035] group-hover:text-brand/10 transition-colors duration-500 select-none">
        {String(i + 1).padStart(2, "0")}
      </div>
      <div className={`flex items-start justify-between mb-6 ${wide ? "lg:mb-0 lg:flex-col lg:gap-4 lg:shrink-0" : ""}`}>
        <span className="grid place-items-center w-12 h-12 rounded-md bg-brand/10 border border-brand/30 text-brand-light group-hover:bg-brand group-hover:text-white transition-colors duration-300">
          <Icon size={22} strokeWidth={2.2} />
        </span>
        <span className="font-mono text-xs text-slate-500 tracking-widest">{String(i + 1).padStart(2, "0")} / {String(SERVICES.length).padStart(2, "0")}</span>
      </div>
      <div className={wide ? "lg:max-w-md" : ""}>
        <h3 className={`font-display font-extrabold uppercase tracking-tight text-2xl text-white mb-3 ${wide ? "lg:text-3xl" : ""}`}>{s.title}</h3>
        <p className={`text-slate-400 text-sm leading-relaxed mb-6 ${wide ? "lg:mb-0" : ""}`}>{s.blurb}</p>
      </div>
      <ul className={`space-y-2 mb-8 flex-1 ${wide ? "lg:mb-0" : ""}`}>
        {s.features.map((f) => (
          <li key={f} className="flex items-center gap-2.5 text-sm text-slate-300">
            <Check size={14} className="text-brand-light shrink-0" strokeWidth={3} /> {f}
          </li>
        ))}
      </ul>
      <button
        onClick={() => onBook(s.title)}
        data-testid={`service-book-btn-${i + 1}`}
        className={`inline-flex items-center gap-2 font-display font-bold uppercase tracking-wider text-sm text-white group/btn ${wide ? "lg:shrink-0" : ""}`}
      >
        Book this service
        <span className="grid place-items-center w-7 h-7 rounded-full border border-white/20 group-hover/btn:bg-brand group-hover/btn:border-brand group-hover/btn:text-white transition-colors duration-300">
          <ArrowUpRight size={14} />
        </span>
      </button>
    </motion.article>
  );
};

export const Services = ({ onBook }) => (
  <section id="services" data-testid="services-section" className="relative py-24 sm:py-32 bg-ink2/60">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <SectionHeader
        id="services"
        eyebrow="What we do"
        lines={["Ten services.", <>One <span className="text-brand-light">standard.</span></>]}
        right="From a quick driveway blast to a full exterior restoration, every job gets the right method, the right pressure and the right chemistry for the surface."
      />
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
        {SERVICES.map((s, i) => (
          <ServiceCard key={s.id} s={s} i={i} onBook={onBook} wide={i === SERVICES.length - 1 && SERVICES.length % 3 === 1} />
        ))}
      </div>
    </div>
  </section>
);
